import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// fetchCasesByPatient accepts a bare patientId or { patientId, recordStatus };
// this normalises the meta.arg back to just the patient id for ownership tracking.
const argPatientId = (arg) =>
    typeof arg === 'object' && arg !== null ? arg.patientId : arg;

// ==================== Initial State ====================
const initialState = {
    cases: [],
    casesPatientId: null,   // which patient the `cases` array belongs to (guards cross-patient leaks)
    casesRequestId: null,   // latest in-flight fetchCasesByPatient request (guards out-of-order responses)
    currentCase: null,
    caseHistory: [], // renamed from attachments
    patientForms: {}, // new key to store the forms data
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all cases
export const fetchCases = createAsyncThunk(
    'cases/fetchAll',
    async (arg, { rejectWithValue }) => {
        // Optional { recordStatus: 'active' | 'completed' | 'all' } filter.
        const recordStatus = typeof arg === 'object' && arg !== null ? arg.recordStatus : undefined;
        try {
            const params = {};
            if (recordStatus && recordStatus !== 'all') params.record_status = recordStatus;
            const response = await api.get('/cases', { params });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch cases'
            );
        }
    }
);

// ==================== NEW: Fetch all cases for a specific patient ====================
export const fetchCasesByPatient = createAsyncThunk(
    'cases/fetchByPatient',
    async (arg, { rejectWithValue }) => {
        // Accept either a bare patientId (legacy callers) or { patientId, recordStatus }.
        const patientId = typeof arg === 'object' && arg !== null ? arg.patientId : arg;
        const recordStatus = typeof arg === 'object' && arg !== null ? arg.recordStatus : undefined;
        try {
            const params = {};
            if (recordStatus && recordStatus !== 'all') params.record_status = recordStatus;
            const response = await api.get(`/cases/patients/${patientId}/cases`, { params });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch cases for this patient'
            );
        }
    }
);

// ==================== Mark a case as completed ====================
export const markCaseComplete = createAsyncThunk(
    'cases/markComplete',
    async (caseId, { rejectWithValue }) => {
        try {
            const response = await api.patch(`/cases/${caseId}/complete`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to mark case as completed'
            );
        }
    }
);


// Fetch single case by ID
export const fetchCase = createAsyncThunk(
    'cases/fetchOne',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/cases/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch case details'
            );
        }
    }
);

// ==================== Store approve/reject case ====================
export const approveOrRejectCase = createAsyncThunk(
    'cases/approveOrReject',
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post('/cases/approve-history', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.data; // new case_history record
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to approve/reject case'
            );
        }
    }
);

// ==================== NEW: Fetch case history by case ID ====================
export const fetchCaseHistory = createAsyncThunk(
    'cases/fetchHistory',
    async (caseId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/cases/${caseId}/history`);
            return response.data.history || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch case history'
            );
        }
    }
);

// ==================== NEW: Check patient forms ====================
export const checkPatientForms = createAsyncThunk(
    'cases/checkPatientForms',
    async ({ patientId, caseId }, { rejectWithValue }) => {
        try {
            const response = await api.get('/cases/patient-case/check', {
                params: { patient_id: patientId, case_id: caseId },
            });
            return response.data; // { forms: {...}, case: {...} }
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to check patient forms'
            );
        }
    }
);

// ==================== Slice ====================
const casesSlice = createSlice({
    name: 'cases',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentCase: (state, action) => {
            state.currentCase = action.payload;
        },
        clearCurrentCase: (state) => {
            state.currentCase = null;
        },
        // Reset the patient-cases list (e.g. when leaving a patient profile)
        clearCases: (state) => {
            state.cases = [];
            state.casesPatientId = null;
            state.casesRequestId = null;
        },
        // Reset everything a Case Profile shows so a previous case never leaks in
        resetCaseProfile: (state) => {
            state.currentCase = null;
            state.patientForms = {};
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL CASES
            .addCase(fetchCases.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCases.fulfilled, (state, action) => {
                state.isLoading = false;
                state.cases = action.payload;
                state.casesPatientId = null; // global list, not scoped to a patient
            })
            .addCase(fetchCases.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // ==================== FETCH CASES BY PATIENT ====================
            .addCase(fetchCasesByPatient.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                // Clear the previous patient's list immediately and mark this as
                // the active request so late/out-of-order responses are ignored.
                state.cases = [];
                state.casesPatientId = null;
                state.casesRequestId = action.meta.requestId;
            })
            .addCase(fetchCasesByPatient.fulfilled, (state, action) => {
                // Ignore responses from a superseded request (patient switched fast).
                if (action.meta.requestId !== state.casesRequestId) return;
                state.isLoading = false;
                state.cases = action.payload;
                state.casesPatientId = String(argPatientId(action.meta.arg)); // the patientId requested
            })
            .addCase(fetchCasesByPatient.rejected, (state, action) => {
                if (action.meta.requestId !== state.casesRequestId) return;
                state.isLoading = false;
                state.error = action.payload;
                state.cases = [];
                state.casesPatientId = String(argPatientId(action.meta.arg));
            })
            // ==================== MARK CASE COMPLETE ====================
            .addCase(markCaseComplete.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(markCaseComplete.fulfilled, (state, action) => {
                state.isLoading = false;
                // Reflect the new record_status in the loaded list right away.
                const updated = action.payload;
                if (updated?.id) {
                    const idx = state.cases.findIndex((c) => c.id === updated.id);
                    if (idx !== -1) state.cases[idx] = { ...state.cases[idx], ...updated };
                }
            })
            .addCase(markCaseComplete.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // FETCH SINGLE CASE
            .addCase(fetchCase.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCase.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentCase = action.payload;
            })
            .addCase(fetchCase.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // APPROVE / REJECT CASE
            .addCase(approveOrRejectCase.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(approveOrRejectCase.fulfilled, (state, action) => {
                state.isLoading = false;
                // Optionally push the new case_history into the current case's history
                state.caseHistory.push(action.payload);
            })
            .addCase(approveOrRejectCase.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // ==================== FETCH CASE HISTORY ====================
            .addCase(fetchCaseHistory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCaseHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.caseHistory = action.payload;
            })
            .addCase(fetchCaseHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // ==================== CHECK PATIENT FORMS ====================
            .addCase(checkPatientForms.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(checkPatientForms.fulfilled, (state, action) => {
                state.isLoading = false;
                // Store forms data and case info
                state.patientForms = action.payload.forms; // new key for forms
                state.currentCase = action.payload.case;    // optionally overwrite currentCase
            })
            .addCase(checkPatientForms.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentCase, clearCurrentCase, clearCases, resetCaseProfile } =
    casesSlice.actions;

export default casesSlice.reducer;
